import { getAdobeDataLayer, getState, getPageLoadPath, push } from './_shared';
import { NAME_PUSH_EVENTS, NAME_QUIZ_EXPERIENCES, NAME_QUIZ_VARIANT } from '../constants';
import { TYPE_PUSH_EVENTS } from '../constants/_internal';
import { AnalyticsError } from '../errors';
import { file } from '../utils';
import { logger } from '../services';
/**
 * Returns a value to help the Analytics team determine the order that a user went through the quiz
 *
 * @param {string} eventName  See the constant, `NAME_PUSH_EVENTS`
 *
 * @see NAME_PUSH_EVENTS
 */

var getIncrementor = function getIncrementor(eventName) {
  var adobeDataLayer = getAdobeDataLayer();
  var incrementor;
  if (eventName === NAME_PUSH_EVENTS.QUIZ_LOAD || typeof adobeDataLayer.getState !== 'function') {
    incrementor = 0;
  } else {
    incrementor = adobeDataLayer.getState('quiz.incrementor') + 1;
  }
  return incrementor;
};
/**
 * Build out event data for starting and stopping quizzes
 *
 * @param {Object} params To build out event data for generic page interactions
 * @param {string} params.eventName
 * @param {string} params.name
 * @param {string} params.experience
 * @param {string} params.quizType
 * @param {Object} [params.additionalVars={}]
 *    Extra fields (aka, Objects) that are sent with the event. These are specific to each event.
 *
 * @return void
 */

var pushEventQuiz = function pushEventQuiz() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    eventName = _ref.eventName,
    name = _ref.name,
    quizType = _ref.quizType,
    experience = _ref.experience,
    _ref$additionalVars = _ref.additionalVars,
    additionalVars = _ref$additionalVars === void 0 ? {} : _ref$additionalVars;

  // make sure nothing is undefined. Unfortunately, JS doesn't do this out
  // of the box for us when we use param destructuring
  if (!eventName || !quizType || !name || !experience) {
    throw new TypeError("The 'eventName|quizType|name|experience' parameters must all be defined. 'eventName': '".concat(eventName, "' | 'quizType': '").concat(quizType, "' | 'name': '").concat(name, "' | 'experience': '").concat(experience, "' "));
  }
  if (!file.getConstantsByPrefix(NAME_PUSH_EVENTS, 'QUIZ_').includes(eventName)) {
    logger.error(new AnalyticsError("The eventName of \"".concat(eventName, "\" is not a valid quiz event. See the NAME_PUSH_EVENTS constant for valid values"), TYPE_PUSH_EVENTS.QUIZ));
  }
  if (!Object.values(NAME_QUIZ_VARIANT).includes(quizType)) {
    logger.error(new AnalyticsError("The quiz type of \"".concat(quizType, "\" is not valid. See the quiz module for valid values"), TYPE_PUSH_EVENTS.QUIZ));
  }
  if (!Object.values(NAME_QUIZ_EXPERIENCES).includes(experience)) {
    logger.error(new AnalyticsError("The quiz experience of \"".concat(experience, "\" is not valid. See the 'NAME_QUIZ_EXPERIENCES' constant for valid values"), TYPE_PUSH_EVENTS.QUIZ));
  }
  var eventData = Object.assign({}, additionalVars, {
    name: "Quiz: ".concat(name),
    'quiz-type': quizType,
    experience: experience,
    incrementor: getIncrementor(eventName)
  });
  push(eventName, {
    quiz: eventData
  });
};
/**
 * Executes whenever a quiz is loaded on the users screen
 *
 * @param {Object} params
 * @param {string} params.name
 *    The name of the quiz. For example, the title
 * @param {string} params.experience
 *    Where the quiz is being loaded. ie, whether it's embedded or not, essentially. See the `NAME_QUIZ_EXPERIENCES`
 *    constant for valid values
 * @param {string} params.quizType
 *    Whether it's a scored quiz (has right/wrong answers) or a questionnaire (no wrong answers)
 *
 * @return void
 */

var load = function load(_ref2) {
  var name = _ref2.name,
    experience = _ref2.experience,
    quizType = _ref2.quizType;
  return pushEventQuiz({
    eventName: NAME_PUSH_EVENTS.QUIZ_LOAD,
    name: name,
    experience: experience,
    quizType: quizType
  });
};
/**
 * When a user goes to the next question, this tracks the info for the question they just completed. To track when the
 * user goes _back_ to a previous question, use the `goBack()` event
 *
 * @param {Object} params
 * @param {string} params.name
 *    The name of the quiz. For example, the title
 * @param {string} params.quizType
 *    Whether it's a scored quiz (has right/wrong answers) or a questionnaire (no wrong answers)
 * @param {string} params.answerText
 *    The text for the answer the user chooses. This is forced to all lowercase for data consistency purposes
 * @param {string} params.answerScore
 *    How the user scored for that question, determined by the quiz's editor. Values are not validated, but are usually
 *    the following: "recommended" | "not recommended" | "correct" | "incorrect". This is forced to all lowercase for
 *    data consistency purposes
 * @param {string} params.stepCompleted
 *    Represents the question that was just answered. Generally in the form, "x of y", but this is not enforced
 *    # TODO is this a good idea to ever enforce? The branched quizzes may have a variable number of questions based on which answers the user chooses
 *
 * @return void
 *
 * @see goBack
 */

var interact = function interact(_ref3) {
  var name = _ref3.name,
    experience = _ref3.experience,
    quizType = _ref3.quizType,
    answerText = _ref3.answerText,
    answerScore = _ref3.answerScore,
    stepCompleted = _ref3.stepCompleted;

  // Valid values for these params (referred to as Objects by analytics) are always
  // a non-empty string so a falsey check is fine
  if (!answerScore || typeof answerScore !== 'string') {
    logger.error(new AnalyticsError("The 'answerScore' param must exist and be a string. Got: '".concat(answerScore, "'"), TYPE_PUSH_EVENTS.QUIZ));
  }
  if (!answerText || typeof answerText !== 'string') {
    logger.error(new AnalyticsError("The 'answerText' param must be set to the text value of the user's answer. Got: '".concat(answerText, "'"), TYPE_PUSH_EVENTS.QUIZ));
  }
  if (!stepCompleted || typeof stepCompleted !== 'string') {
    logger.error(new AnalyticsError("The 'stepCompleted' param must exist and be a string. Got: '".concat(stepCompleted, "'"), TYPE_PUSH_EVENTS.QUIZ));
  } // These are all lowercase to help the data team organize their data. It was a request from them.

  var additionalVars = {
    'answer-text': answerText.toLowerCase(),
    'answer-score': answerScore.toLowerCase(),
    'step-completed': stepCompleted.toLowerCase()
  };
  return pushEventQuiz({
    eventName: NAME_PUSH_EVENTS.QUIZ_INTERACT,
    name: name,
    experience: experience,
    quizType: quizType,
    additionalVars: additionalVars
  });
};
/**
 * When the results screen is displayed
 *
 * @param {Object} params
 * @param {string} params.name
 *    The name of the quiz. For example, the title
 * @param {string} params.quizType
 *    Whether it's a scored quiz (has right/wrong answers) or a questionnaire (no wrong answers)
 * @param {string} params.quizScore
 *    Overall score on the quiz. e.g., 'recommended', 'correct'
 *    TODO this may change. See GEL-3249
 * @param {string} params.resultMessage
 *    The primary headline/title of the results slide/screen
 *
 * @return void
 */

var complete = function complete(_ref4) {
  var name = _ref4.name,
    experience = _ref4.experience,
    quizType = _ref4.quizType,
    quizScore = _ref4.quizScore,
    resultMessage = _ref4.resultMessage;
  if (!resultMessage) {
    logger.error(new AnalyticsError("The 'resultMessage' param, which is the headline or title of the results page, must be set. Got: '".concat(resultMessage, "'"), TYPE_PUSH_EVENTS.QUIZ));
  }
  if (!quizScore) {
    logger.error(new AnalyticsError("The 'quizScore' param, which is the overall score on the quiz, must be set. Got: '".concat(quizScore, "'"), TYPE_PUSH_EVENTS.QUIZ));
  }
  var additionalVars = {
    'result-message': resultMessage,
    'quiz-score': quizScore
  };
  return pushEventQuiz({
    eventName: NAME_PUSH_EVENTS.QUIZ_COMPLETE,
    name: name,
    experience: experience,
    quizType: quizType,
    additionalVars: additionalVars
  });
};
/**
 * When the user clicks the button to retake a quiz
 *
 * @param {Object} params
 * @param {string} params.name
 *    The name of the quiz. For example, the title
 * @param {string} params.quizType
 *    Whether it's a scored quiz (has right/wrong answers) or a questionnaire (no wrong answers)
 * @param {string} params.quizScore
 *    Overall score on the quiz. e.g., 'recommended', 'correct'
 * @param {string} params.resultMessage
 *    The primary headline/title of the results slide/screen
 *
 * @return void
 */

var retake = function retake(_ref5) {
  var name = _ref5.name,
    experience = _ref5.experience,
    quizType = _ref5.quizType,
    quizScore = _ref5.quizScore,
    resultMessage = _ref5.resultMessage;
  if (!resultMessage) {
    logger.error(new AnalyticsError("The 'resultMessage' param, which is the headline or title of the results page, must be set. Got: '".concat(resultMessage, "'"), TYPE_PUSH_EVENTS.QUIZ));
  }
  if (!quizScore) {
    logger.error(new AnalyticsError("The 'quizScore' param, which is the overall score on the quiz, must be set. Got: '".concat(quizScore, "'"), TYPE_PUSH_EVENTS.QUIZ));
  }
  var additionalVars = {
    'result-message': resultMessage,
    'quiz-score': quizScore
  };
  return pushEventQuiz({
    eventName: NAME_PUSH_EVENTS.QUIZ_RETAKE,
    name: name,
    experience: experience,
    quizType: quizType,
    additionalVars: additionalVars
  });
};
/**
 * When the user goes back to a previous question. For retaking the entire quiz, use `retake`
 *
 * @param {Object} params
 * @param {string} params.name
 *    The name of the quiz. For example, the title
 * @param {string} params.quizType
 *    Whether it's a scored quiz (has right/wrong answers) or a questionnaire (no wrong answers)
 *
 * @return void
 * @see retake
 */

var goBack = function goBack(_ref6) {
  var name = _ref6.name,
    experience = _ref6.experience,
    quizType = _ref6.quizType;
  return pushEventQuiz({
    eventName: NAME_PUSH_EVENTS.QUIZ_GO_BACK,
    name: name,
    experience: experience,
    quizType: quizType
  });
};
/**
 * When the user clicks on the quiz's CTA.
 *
 * These are only for CTA clicks on the results page, NOT any before, like a  "start quiz" button
 *
 * @param {Object} params
 * @param {string} params.name
 *    The name of the quiz. For example, the title
 * @param {string} params.quizType
 *    Whether it's a scored quiz (has right/wrong answers) or a questionnaire (no wrong answers)
 * @param {string} params.quizScore
 *    Overall score on the quiz. e.g., 'recommended', 'correct'
 * @param {string} params.resultMessage
 *    The primary headline/title of the results slide/screen
 * @param {string} params.ctaValue
 *    The label of the CTA button/element
 *
 * @return void
 */

var clickCta = function clickCta(_ref7) {
  var name = _ref7.name,
    experience = _ref7.experience,
    quizType = _ref7.quizType,
    quizScore = _ref7.quizScore,
    resultMessage = _ref7.resultMessage,
    ctaValue = _ref7.ctaValue;
  if (!resultMessage) {
    logger.error(new AnalyticsError("The 'resultMessage' param, which is the headline or title of the results page, must be set. Got: '".concat(resultMessage, "'"), TYPE_PUSH_EVENTS.QUIZ));
  }
  if (!ctaValue) {
    logger.error(new AnalyticsError("The 'ctaValue' param, which is the label of the CTA on the results page, must be set. Got: '".concat(ctaValue, "'"), TYPE_PUSH_EVENTS.QUIZ));
  }
  if (!quizScore) {
    logger.error(new AnalyticsError("The 'quizScore' param, which is the overall score on the quiz, must be set. Got: '".concat(quizScore, "'"), TYPE_PUSH_EVENTS.QUIZ));
  }
  var additionalVars = {
    'cta-value': ctaValue,
    'result-message': resultMessage,
    'quiz-score': quizScore
  };
  return pushEventQuiz({
    eventName: NAME_PUSH_EVENTS.QUIZ_CTA,
    name: name,
    experience: experience,
    quizType: quizType,
    additionalVars: additionalVars
  });
};
export { load, interact, complete, retake, goBack, clickCta };